import { useEffect, useMemo, useState } from 'react';
import { BiCalendarEvent, BiChevronRight } from 'react-icons/bi';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/Skeleton';
import type { EventResponse } from '@/store/events/events.dto';
import { useEvents } from '@/store/events/events.store';
import { formatEventDateRange } from '@/utils/formatEventDateRange';
import { formatType } from '@/utils/formatType';

import { BaseWidget } from '../BaseWidget';

import s from './NextEventWidget.module.scss';

const PLAN_ROUTE = '/plan';
const UPCOMING_LIMIT = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const kickerFor = (startIso: string) => {
  const days = Math.round(
    (startOfDay(new Date(startIso)).getTime() - startOfDay(new Date()).getTime()) / DAY_MS,
  );
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
};

const metaFor = (event: EventResponse) =>
  [formatEventDateRange(event.time_slot?.start, event.time_slot?.end), event.location?.name]
    .filter(Boolean)
    .join(' · ');

const eventLink = (id: string) => `${PLAN_ROUTE}?event=${id}&view=week`;

export const NextEventWidget = () => {
  const { eventsList, getAllEvents } = useEvents();
  const [openedAt] = useState(() => new Date().getTime());
  // The store's `isLoading` is still false on the first paint, which drew "Nothing
  // scheduled" for a frame; and a failed read leaves no error behind to stop on.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    getAllEvents()
      .catch(console.error)
      .finally(() => setSettled(true));
  }, [getAllEvents]);

  const upcoming = useMemo(
    () =>
      (eventsList?.items ?? [])
        .filter(
          (event) =>
            !event.parent &&
            !!event.time_slot?.end &&
            new Date(event.time_slot.end).getTime() >= openedAt,
        )
        .sort((a, b) => (a.time_slot?.start ?? '').localeCompare(b.time_slot?.start ?? '')),
    [eventsList, openedAt],
  );

  const [nextEvent, ...rest] = upcoming;
  const alsoComing = rest.slice(0, UPCOMING_LIMIT);

  if (!eventsList && !settled) {
    return (
      <BaseWidget headerTitle="Upcoming Events" headerIcon={<BiCalendarEvent />}>
        <div aria-busy="true">
          <div className={s.heroSkeleton}>
            <Skeleton variant="text" width={140} height={12} />
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="45%" height={14} />
          </div>
          <div className={s.upcomingBlock}>
            <Skeleton variant="text" width={120} height={12} className={s.upcomingHead} />
            <ul className={s.eventList}>
              {Array.from({ length: UPCOMING_LIMIT }, (_, i) => (
                <li key={i} className={s.eventRowSkeleton}>
                  <Skeleton variant="text" width={110} height={13} />
                  <Skeleton variant="text" width="40%" height={14} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </BaseWidget>
    );
  }

  return (
    <BaseWidget headerTitle="Upcoming Events" headerIcon={<BiCalendarEvent />}>
      {nextEvent ? (
        <Link className={s.hero} to={eventLink(nextEvent.id)}>
          <span className={s.heroText}>
            <span className={s.kicker}>
              Next event · {kickerFor(nextEvent.time_slot?.start ?? '')}
            </span>
            <span className={`headline-small ${s.heroTitle}`} title={nextEvent.title}>
              {nextEvent.title}
            </span>
            <span className={s.meta}>{metaFor(nextEvent)}</span>
          </span>
          <BiChevronRight className={s.heroChevron} aria-hidden="true" />
        </Link>
      ) : (
        <div className={s.hero}>
          <span className={s.heroText}>
            <span className={s.kicker}>Next event</span>
            <span className="headline-small">Nothing scheduled</span>
            <span className={s.meta}>Upcoming events will show up here.</span>
          </span>
        </div>
      )}

      {alsoComing.length > 0 && (
        <div className={s.upcomingBlock}>
          <div className={s.upcomingHead}>
            <span className={s.upcomingLabel}>Also coming up</span>
            <Link className={s.drill} to={`${PLAN_ROUTE}?view=week`}>
              All events <BiChevronRight aria-hidden="true" />
            </Link>
          </div>
          <ul className={s.eventList}>
            {alsoComing.map((event) => (
              <li key={event.id}>
                <Link className={s.eventRow} to={eventLink(event.id)}>
                  <span className={s.eventDate}>
                    {formatEventDateRange(event.time_slot?.start, event.time_slot?.end)}
                  </span>
                  <span className={s.eventTitle}>{event.title}</span>
                  <span className={s.eventType}>{formatType(event.type)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </BaseWidget>
  );
};

export default NextEventWidget;
